import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Logger from "../../../services/Logger";
import Clear from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Card,
    CardContent,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    Box as MuiBox,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UtilService from "../../../services/UtilService";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomButton from "../../components/CustomButton";
import CustomSnackbar from "../../components/CustomSnackbar";
import { patternSearch } from "../../pzl_common/patternSearch";
import PuzRow from "../../pzl_common/PuzRow";
import WordMeaning from "../../pzl_common/WordMeaning";
import EditFilledCoordinatesDialog from "../../pzl_common/EditFilledCoordinatesDialog";
import AdminCrossBoardService, { PUBLISHED } from "../board/AdminCrossBoardService";
import { getLocalizedStoryFromTemplate } from "../../pzl_common/getEnStoryFromTemplate";
import AdminCrossService from "./AdminCrossService";

function normalizeCrossTplTagList(raw) {
    if (raw == null) return [];
    if (typeof raw === "string" && raw.trim()) {
        return raw
            .split(/[,;\n]/)
            .map((s) => s.trim())
            .filter(Boolean);
    }
    if (!Array.isArray(raw)) return [];
    return raw
        .map((t) => {
            if (typeof t === "string") return t.trim();
            return String(t?.tag ?? t?.name ?? t?.label ?? "").trim();
        })
        .filter(Boolean);
}

/** Tags on GET /v1/puzzle/cross-tpl/:id — root / template first, then topic fallback. */
function tagsFromCrossTplResponse(r) {
    if (!r) return [];
    const direct = normalizeCrossTplTagList(r.tags ?? r.template?.tags);
    if (direct.length) return direct;
    return normalizeCrossTplTagList(r.topic?.tags);
}

export default function AdminCrossTplDetail() {
    const { crossTplId } = useParams();
    const navigate = useNavigate();

    const [title, setTitle] = useState("");
    const [templateTags, setTemplateTags] = useState([]);
    const [templateLanguage, setTemplateLanguage] = useState("english");
    const [createdAt, setCreatedAt] = useState("");
    const [hiddenWordCount, setHiddenWordCount] = useState(0);
    const [hiddenMessageLength, sethiddenMessageLength] = useState(0);
    const [wordsInUse, setWordsInUse] = useState("");
    const [wordsNotInUse, setWordsNotInUse] = useState("");

    const [coordinates, setCoordinates] = useState();
    const [filledCoordinates, setFilledCoordinates] = useState();
    const [square, setSquare] = useState([]);
    const [reload, setReload] = useState();
    const [openDelete, setOpenDelete] = useState(false);
    const [openEditFilledCoords, setOpenEditFilledCoords] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState(false);
    const [message, setMessage] = useState("");
    const [searchWord, setSearchWord] = useState("");
    const [positions, setPositions] = useState([]);
    const [genStoryLoading, setGenStoryLoading] = useState(false);
    const [templateState, setTemplateState] = useState(null);
    const [storyEnTitle, setStoryEnTitle] = useState("");
    const [storyEnText, setStoryEnText] = useState("");
    const [storyImagePrompt, setStoryImagePrompt] = useState("");
    const [storyImageUrl, setStoryImageUrl] = useState("");
    const [highlightWordsInUse, setHighlightWordsInUse] = useState(true);

    // New state for word meaning display
    const [selectedWord, setSelectedWord] = useState(null);
    const [showWordMeaning, setShowWordMeaning] = useState(false);

    // Fit grid to container: 12 cells × 50px + 11 gaps × 1px = 611px
    const GRID_PX = 611;
    const gridContainerRef = useRef(null);
    const [gridScale, setGridScale] = useState(1);

    useEffect(() => {
        if (!gridContainerRef.current || square.length === 0) return;
        const el = gridContainerRef.current;
        const updateScale = () => {
            const w = el.clientWidth;
            const h = el.clientHeight;
            const scale = Math.min(1, w / GRID_PX, h / GRID_PX);
            setGridScale(scale);
        };
        updateScale();
        let roRaf = null;
        const ro = new ResizeObserver(() => {
            if (roRaf != null) cancelAnimationFrame(roRaf);
            roRaf = requestAnimationFrame(() => {
                roRaf = null;
                updateScale();
            });
        });
        ro.observe(el);
        return () => {
            if (roRaf != null) cancelAnimationFrame(roRaf);
            ro.disconnect();
        };
    }, [square.length]);

    useEffect(() => {
        // Logger.log(crossTplId);
        AdminCrossService.requestTemplate(crossTplId)
            .then((r) => {
                // Logger.log(r);

                setTitle(r.title ?? "");
                setTemplateTags(tagsFromCrossTplResponse(r));
                setTemplateState(r.state ?? null);
                setTemplateLanguage(r.language === "french" ? "french" : "english");
                setCreatedAt(UtilService.formatDate(r.createdAt));
                setHiddenWordCount(r.hiddenWordCount);
                sethiddenMessageLength(r.hiddenMessageLength);
                setWordsInUse(r.wordsInUse);
                setWordsNotInUse(r.wordsNotInUse);

                const { title, text, imagePrompt, imageUrl } = getLocalizedStoryFromTemplate(r);
                setStoryEnTitle(title);
                setStoryEnText(text);
                setStoryImagePrompt(typeof imagePrompt === "string" ? imagePrompt : "");
                setStoryImageUrl(typeof imageUrl === "string" ? imageUrl : "");

                const parsedCoordinates = JSON.parse(r.coordinates);
                setCoordinates(parsedCoordinates);

                const filledCoordinates = JSON.parse(r.filledCoordinates);
                setFilledCoordinates(filledCoordinates);
                // Logger.log(JSON.parse(r.filledCoordinates));

                // Assuming that coordinates contains information about positions
                const newSquare = initializeSquare(filledCoordinates);
                setSquare(newSquare);
            })
            .catch((e) => Logger.log(e));
    }, [crossTplId, reload]);

    // Function to initialize the square array
    const initializeSquare = (filledCoordinates) => {
        const size = 12;
        let newSquare = Array.from({ length: size }, () => Array(size).fill(0));

        // Logger.log("before", newSquare);
        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                // Logger.log(row + ", " + col);
                newSquare[row][col] = findCharacter(row, col, filledCoordinates);
            }
        }
        // Logger.log("after", newSquare);

        return newSquare;
    };

    const findCharacter = (row, col, filledCoordinates) => {
        let found = "";

        if (filledCoordinates) {
            // Logger.log(row + ", " + col);
            found = filledCoordinates
                .map((c) => c.positions)
                .map((positions) => {
                    const matchingPosition = positions.find((p) => p.row === row && p.col === col);

                    if (matchingPosition) {
                        // Logger.log(matchingPosition.value);
                        return matchingPosition.value ? matchingPosition.value.toUpperCase() : "";
                    }
                    return "";
                })
                .find((value) => value !== "");

            // Logger.log(found);
        }
        // Logger.log(found)

        return found;
    };



    const handleDelete = () => {
        AdminCrossService.deleteTemplate(crossTplId).then(() => {
            handleSnackBar("warning", "deleted");
            navigate("/admin/puzzle/cross_tpl");
        });
    };

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };

    const handleGenStory = async () => {
        if (!crossTplId) return;
        setGenStoryLoading(true);
        try {
            await AdminCrossService.genStory(crossTplId, {});
            handleSnackBar("success", "Story generation started.");
            setReload(Date.now());
        } catch (e) {
            Logger.log(e);
            const msg = e?.response?.message || e?.message || "Generate story failed.";
            handleSnackBar("error", msg);
        } finally {
            setGenStoryLoading(false);
        }
    };

    const handleWordSearch = (e) => {
        const str = e.target.value.toUpperCase();
        setSearchWord(str);


        // Logger.log(square, str)
        const foundPositions = patternSearch(square, str);
        setPositions(foundPositions);

        // Logger.log(foundPositions)
    };

    // When a grid cell is clicked, find and highlight its word
    const handleCellClick = (row, col) => {
        if (!filledCoordinates) return;
        const words = getWordsFromCoordinates(filledCoordinates);
        const wordObj = words.find(
            (w) => w.positions && w.positions.some((p) => p.row === row && p.col === col)
        );
        if (wordObj) {
            setSearchWord(wordObj.displayText);
            setPositions(wordObj.positions || []);
            setSelectedWord(wordObj.text);
            setShowWordMeaning(true);
        }
    };

    // Function to handle word chip click
    const handleWordClick = (displayText) => {
        // Find the original word object to get the original text for API call
        const wordObj = getWordsFromCoordinates(filledCoordinates).find(w => w.displayText === displayText);
        const originalText = wordObj ? wordObj.text : displayText;
        
        setSelectedWord(originalText);
        setShowWordMeaning(true);

        // Set the search word and find positions to highlight in the puzzle
        setSearchWord(originalText);
        const foundPositions = patternSearch(square, originalText);
        setPositions(foundPositions);
    };

    // Function to close word meaning display

    // Function to save definition (placeholder)
    const handleSaveDefinition = (definition, word, crossTplId) => {
        Logger.log("Saving definition:", definition, "for word:", word, "in template:", crossTplId);
        // Implement your save logic here
    };

    // Helper function to get words from filledCoordinates with reversed text handling
    const getWordsFromCoordinates = (coordinates) => {
        if (!coordinates) return [];
        return coordinates
            .filter(wordObj => wordObj.text !== null && wordObj.text !== undefined) // Skip null/undefined text
            .map(wordObj => {
                // If reversed is true, reverse the text
                const displayText = wordObj.reversed ? wordObj.text.split('').reverse().join('') : wordObj.text;
                return {
                    ...wordObj,
                    displayText: displayText
                };
            });
    };

    // Helper function to split words string into an array (for backward compatibility)
    const getWordsArray = (wordsString) => {
        if (!wordsString) return [];
        return wordsString.split("\n").map((word) => word.trim()).filter((word) => word.length > 0);
    };

    /** True if `wordObj`’s text or displayText appears as a whole word in `storyText` (same rules as highlights). */
    const isWordUsedInStoryText = (storyText, wordObj) => {
        if (!storyText || !String(storyText).trim()) return false;
        const tokenSet = new Set();
        if (wordObj.text) tokenSet.add(String(wordObj.text).trim().toLowerCase());
        if (wordObj.displayText) tokenSet.add(String(wordObj.displayText).trim().toLowerCase());
        if (tokenSet.size === 0) return false;
        const parts = String(storyText).split(/(\b[\w']+\b)/g);
        for (const part of parts) {
            if (/^[\w']+$/.test(part) && tokenSet.has(part.toLowerCase())) {
                return true;
            }
        }
        return false;
    };

    const wordsInUseNotInStory = useMemo(() => {
        if (!storyEnText || !String(storyEnText).trim()) return [];
        const inUse = getWordsFromCoordinates(filledCoordinates);
        return inUse.filter((w) => !isWordUsedInStoryText(storyEnText, w));
    }, [storyEnText, filledCoordinates]);

    const storyTextBlockSx = {
        whiteSpace: "pre-wrap",
        lineHeight: 1.7,
        minHeight: 200,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
        bgcolor: "action.hover",
    };

    /** Story body: optional highlight + bold for words in use (case-insensitive, whole tokens). */
    const renderEnglishStoryWithHighlights = (storyText) => {
        if (!storyText || !String(storyText).trim()) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No English story yet. Use Generate Story to create one.
                </Typography>
            );
        }
        const text = String(storyText);
        if (!highlightWordsInUse) {
            return (
                <Typography component="div" variant="body2" sx={storyTextBlockSx}>
                    {text}
                </Typography>
            );
        }
        const inUse = getWordsFromCoordinates(filledCoordinates);
        const matchSet = new Set();
        inUse.forEach((w) => {
            if (w.text) matchSet.add(String(w.text).trim().toLowerCase());
            if (w.displayText) matchSet.add(String(w.displayText).trim().toLowerCase());
        });
        const parts = text.split(/(\b[\w']+\b)/g);
        return (
            <Typography component="div" variant="body2" sx={storyTextBlockSx}>
                {parts.map((part, i) => {
                    const isWord = /^[\w']+$/.test(part);
                    if (isWord && matchSet.has(part.toLowerCase())) {
                        return (
                            <Box
                                key={i}
                                component="mark"
                                sx={{
                                    bgcolor: "warning.light",
                                    color: "text.primary",
                                    fontWeight: "bold",
                                    px: 0.25,
                                    borderRadius: 0.5,
                                }}
                            >
                                {part}
                            </Box>
                        );
                    }
                    return <span key={i}>{part}</span>;
                })}
            </Typography>
        );
    };

    return (
        <Box sx={{ width: "100%" }}>
            {/* Header Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <IconButton 
                    onClick={() => navigate(-1)} 
                    sx={{ 
                        color: 'grey.700',
                        '&:hover': {
                            backgroundColor: 'grey.100'
                        }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25, mb: 1 }}>
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{ fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.3 }}
                        >
                            {title?.trim() ? title : "N/A"}
                        </Typography>
                        {templateTags.map((tag, i) => (
                            <Chip
                                key={`${tag}-${i}`}
                                label={tag}
                                size="small"
                                variant="outlined"
                                color="primary"
                            />
                        ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Created: {createdAt || "N/A"}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <CustomButton
                        onClick={() => setOpenEditFilledCoords(true)}
                        variant="outlined"
                        sx={{ height: "40px" }}
                    >
                        Edit filledCoordinates
                    </CustomButton>
                    <Tooltip
                        title={
                            templateState === PUBLISHED
                                ? "Cannot delete: this template is published."
                                : "Delete template"
                        }
                    >
                        <span>
                            <IconButton
                                color="warning"
                                disabled={templateState === PUBLISHED}
                                onClick={() => setOpenDelete(true)}
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "warning.light",
                                    },
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* Main Content */}
            <Grid container spacing={3}>
                {/* Left Column: Puzzle Grid */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Puzzle Grid
                            </Typography>
                            <Box
                                ref={gridContainerRef}
                                sx={{
                                    width: "100%",
                                    maxHeight: "min(70vh, 640px)",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "flex-start",
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: GRID_PX * gridScale,
                                        height: GRID_PX * gridScale,
                                        flexShrink: 0,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            width: GRID_PX,
                                            height: GRID_PX,
                                            transform: `scale(${gridScale})`,
                                            transformOrigin: "top left",
                                        }}
                                    >
                                        {square.map((row, rowIndex) => (
                                            <div key={rowIndex}>
                                                <PuzRow
                                                    rowIndex={rowIndex}
                                                    row={row}
                                                    selectedWord={searchWord}
                                                    positions={positions}
                                                    onCellClick={handleCellClick}
                                                />
                                            </div>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                                <TextField
                                    label="Word Search"
                                    value={searchWord}
                                    onChange={handleWordSearch}
                                    helperText="Type the word you want to find in the puzzle"
                                    variant="outlined"
                                    size="small"
                                    sx={{ minWidth: 200 }}
                                />
                                {searchWord && (
                                    <CustomButton
                                        onClick={() => {
                                            setSearchWord("");
                                            setPositions([]);
                                            setSelectedWord(null);
                                            setShowWordMeaning(false);
                                        }}
                                        color="secondary"
                                        variant="outlined"
                                        startIcon={<Clear />}
                                        size="small"
                                        sx={{ height: "40px" }}
                                    >
                                        Clear
                                    </CustomButton>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column: Words Lists */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Card sx={{ height: "100%" }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Words In Use ({getWordsFromCoordinates(filledCoordinates).length})
                                    </Typography>
                                    <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 400, overflow: 'auto', alignContent: 'flex-start' }}>
                                        {getWordsFromCoordinates(filledCoordinates).map((wordObj, index) => (
                                            <Chip
                                                key={index}
                                                label={wordObj.displayText}
                                                variant={selectedWord === wordObj.displayText ? "filled" : "outlined"}
                                                color="primary"
                                                clickable
                                                onClick={() => handleWordClick(wordObj.displayText)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedWord === wordObj.displayText ? 'primary.main' : 'transparent',
                                                    color: selectedWord === wordObj.displayText ? 'white' : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor: selectedWord === wordObj.displayText ? 'primary.dark' : 'primary.light'
                                                    }
                                                }}
                                                size="small"
                                            />
                                        ))}
                                    </MuiBox>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Card sx={{ height: "100%" }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Words Not In Use ({getWordsArray(wordsNotInUse).length})
                                    </Typography>
                                    <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 400, overflow: 'auto', alignContent: 'flex-start' }}>
                                        {getWordsArray(wordsNotInUse).map((word, index) => (
                                            <Chip
                                                key={index}
                                                label={word}
                                                variant={selectedWord === word ? "filled" : "outlined"}
                                                color="secondary"
                                                clickable
                                                onClick={() => handleWordClick(word)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedWord === word ? 'secondary.main' : 'transparent',
                                                    color: selectedWord === word ? 'white' : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor: selectedWord === word ? 'secondary.dark' : 'secondary.light'
                                                    }
                                                }}
                                                size="small"
                                            />
                                        ))}
                                    </MuiBox>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            {/* Shared Word Meaning Display */}
                            {showWordMeaning && selectedWord && (
                                <Card sx={{ mt: 2 }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Meaning for: {selectedWord}
                                        </Typography>
                                        <WordMeaning
                                            selectedWord={selectedWord}
                                            onSaveDefinition={handleSaveDefinition}
                                            gridTplId={crossTplId}
                                            showClueButton={false}
                                            language={templateLanguage}
                                        />
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {templateLanguage === "french" ? "French story" : "English story"}
                    </Typography>
                    {storyImagePrompt ? (
                        <TextField
                            label="Image prompt"
                            value={storyImagePrompt}
                            fullWidth
                            multiline
                            minRows={2}
                            margin="normal"
                            size="small"
                            InputProps={{ readOnly: true }}
                            sx={{ mb: 2 }}
                        />
                    ) : null}
                    <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
                        <CustomButton
                            onClick={handleGenStory}
                            disabled={genStoryLoading}
                            variant="contained"
                            color="primary"
                            sx={{ height: "40px" }}
                        >
                            {genStoryLoading ? "Generating…" : "Generate Story"}
                        </CustomButton>
                        <FormControlLabel
                            sx={{
                                m: 0,
                                ml: { xs: 0, sm: 1 },
                                alignItems: "flex-start",
                            }}
                            control={
                                <Checkbox
                                    checked={highlightWordsInUse}
                                    onChange={(e) => setHighlightWordsInUse(e.target.checked)}
                                />
                            }
                            label={
                                <Box sx={{ pt: 0.25 }}>
                                        <Typography component="span" variant="body2">
                                            Highlight words in use
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Story text: words in use highlighted and bold when on.
                                        </Typography>
                                </Box>
                            }
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 2,
                            alignItems: { xs: "stretch", md: "flex-start" },
                        }}
                    >
                        {storyImageUrl ? (
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    alignSelf: { xs: "center", md: "flex-start" },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={storyImageUrl}
                                    alt="Story illustration"
                                    sx={{
                                        display: "block",
                                        width: { xs: "100%", md: 280 },
                                        maxWidth: "100%",
                                        height: "auto",
                                        maxHeight: { md: 360 },
                                        objectFit: "contain",
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: "divider",
                                        bgcolor: "action.hover",
                                    }}
                                />
                            </Box>
                        ) : null}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {storyEnTitle ? (
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    {storyEnTitle}
                                </Typography>
                            ) : null}
                            {renderEnglishStoryWithHighlights(storyEnText)}
                        </Box>
                    </Box>
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Words in use not found in this story ({wordsInUseNotInStory.length})
                        </Typography>
                        <MuiBox
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                alignContent: "flex-start",
                            }}
                        >
                            {wordsInUseNotInStory.map((wordObj, index) => (
                                <Chip
                                    key={`${wordObj.text ?? ""}-${index}`}
                                    label={wordObj.displayText ?? wordObj.text}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                />
                            ))}
                        </MuiBox>
                    </Box>
                </CardContent>
            </Card>

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />

            <ConfirmDialog
                open={openDelete}
                setOpen={setOpenDelete}
                title="Delete Template?"
                message="This action cannot be undone. All puzzles created from this template will also be deleted."
                targetId={crossTplId}
                callback={handleDelete}
            />

            <EditFilledCoordinatesDialog
                open={openEditFilledCoords}
                onClose={() => setOpenEditFilledCoords(false)}
                initialData={filledCoordinates}
                onSave={async (data) => {
                    await AdminCrossBoardService.updatePuzzleCoordinates(crossTplId, data);
                    handleSnackBar("success", "Saved");
                    setReload({});
                }}
            />
        </Box>
    );
}

